import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Slide,
} from '@mui/material';
import MaterialSymbol from '../common/MaterialSymbol';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslate } from 'react-admin';
import { Badge } from '../../services/gamificationService';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface BadgeNotificationProps {
    badge: Badge | null;
    open: boolean;
    onClose: () => void;
}

const BadgeNotification: React.FC<BadgeNotificationProps> = ({
    badge,
    open,
    onClose,
}) => {
    const translate = useTranslate();
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        if (open) {
            setShowConfetti(true);
            const timer = setTimeout(() => setShowConfetti(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [open]);

    if (!badge) return null;

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'visible',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                },
            }}
        >
            <DialogContent sx={{ textAlign: 'center', py: 4, position: 'relative' }}>
                {/* Confetti effect */}
                {showConfetti && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            overflow: 'hidden',
                            pointerEvents: 'none',
                        }}
                    >
                        {[...Array(20)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    position: 'absolute',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#ff9ff3'][i % 5],
                                    left: `${Math.random() * 100}%`,
                                    top: -20,
                                    animation: `confetti-fall ${1.5 + Math.random()}s ease-out forwards`,
                                    animationDelay: `${Math.random() * 0.5}s`,
                                    '@keyframes confetti-fall': {
                                        '0%': {
                                            transform: 'translateY(0) rotate(0deg)',
                                            opacity: 1,
                                        },
                                        '100%': {
                                            transform: `translateY(400px) rotate(${360 + Math.random() * 360}deg)`,
                                            opacity: 0,
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Badge icon with glow effect */}
                <Box
                    sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        boxShadow: '0 0 40px rgba(255, 255, 255, 0.3)',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%, 100%': {
                                boxShadow: '0 0 40px rgba(255, 255, 255, 0.3)',
                            },
                            '50%': {
                                boxShadow: '0 0 60px rgba(255, 255, 255, 0.5)',
                            },
                        },
                    }}
                >
                    <MaterialSymbol
                        name={badge.icon || 'emoji_events'}
                        size={64}
                        filled
                        style={{
                            color: '#ffd700',
                            filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))',
                        }}
                    />
                </Box>

                {/* Celebration text */}
                <Typography
                    variant="h5"
                    sx={{
                        color: 'white',
                        fontWeight: 700,
                        mb: 1,
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                >
                    {translate('gamification.badgeEarned', { _: 'Auszeichnung verdient!' })}
                </Typography>

                {/* Badge name */}
                <Typography
                    variant="h4"
                    sx={{
                        color: '#ffd700',
                        fontWeight: 800,
                        mb: 2,
                        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                >
                    {badge.name}
                </Typography>

                {/* Badge description */}
                {badge.description && (
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            mb: 3,
                        }}
                    >
                        {badge.description}
                    </Typography>
                )}

                {/* Close button */}
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        bgcolor: 'white',
                        color: '#764ba2',
                        fontWeight: 600,
                        px: 4,
                        py: 1,
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                        },
                    }}
                >
                    {translate('gamification.awesome', { _: 'Super!' })}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default BadgeNotification;
